import React, { useState, useEffect } from "react";
import {
  useMediaQuery,
  Select,
  MenuItem,
  Typography,
  Button,
  Grid,
  Paper,
  ListItemText,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { lighten, darken, alpha } from "@mui/material/styles";
import DiceModifiers from "./Util/DiceModifiers";
import FreeRanks from "./Util/FreeRanks";
import getDicePool from "./Util/GetDicePool";
import getFullBoughtTalents from "./Util/GetFullBoughtTalents";
import characteristicsData from "./data/characteristics.json"
import talentsData from "./data/talents.json"
import forcePowersData from "./data/forcePowers.json";
import forceAbilitiesData from "./data/forceAbilities.json";
import skillsData from "./data/skills.json";
import { getEffective } from "./Util/applyHomebrew";
import { isSpeciesCareerSkill } from "./Util/SpeciesSkillMods";

let forcePowers = forcePowersData
let forceAbilities = forceAbilitiesData
let skills = skillsData
let talents = talentsData

function App({ character, handleCharacterChange, appFiles }) {
  const [characteristics, setCharacteristics] = useState({
    Brawn: parseInt(character.Characteristics.Brawn) + (character.Dedications?.Brawn?.length || 0),
    Agility: parseInt(character.Characteristics.Agility) + (character.Dedications?.Agility?.length || 0),
    Intellect: parseInt(character.Characteristics.Intellect) + (character.Dedications?.Intellect?.length || 0),
    Cunning: parseInt(character.Characteristics.Cunning) + (character.Dedications?.Cunning?.length || 0),
    Willpower: parseInt(character.Characteristics.Willpower) + (character.Dedications?.Willpower?.length || 0),
    Presence: parseInt(character.Characteristics.Presence) + (character.Dedications?.Presence?.length || 0),
  });
  const [usedStartingXP, setUsedStartingXP] = useState(character.UsedStartingXP || 0)
  const [leftSkills, setLeftSkills] = useState([])
  const [rightSkills, setRightSkills] = useState([])
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isStacked = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  forcePowers = getEffective('forcePowers', forcePowersData);
  forceAbilities = getEffective('forceAbilities', forceAbilitiesData);
  skills = getEffective('skills', skillsData);
  talents = getEffective('talents', talentsData);

  const dieModifiers = DiceModifiers(character, handleCharacterChange, forcePowers, forceAbilities, skills)
  const freeRanks = FreeRanks(character);
  const baseFreeRanks = FreeRanks(character, { includeGear: false });

  const { Bought: boughtTalents, universalForce } = getFullBoughtTalents(character);

  const hasDedication = (char) => {
    if (character.Dedications?.[char]) {
      return character.Dedications[char].length
    } else {
      return false
    }
  }

  let charMods = {}
  for (let t in boughtTalents) {
    let tal = boughtTalents[t].data
    if (tal.SkillChars && tal.SkillChars.SkillChar) {
      const entries = Array.isArray(tal.SkillChars.SkillChar) ? tal.SkillChars.SkillChar : [tal.SkillChars.SkillChar]
      entries.forEach((entry) => {
        if (!entry || !entry.SkillKey) { return }
        charMods[entry.SkillKey] = entry.CharKeys ? entry.CharKeys : entry.CharKey
      })
    }
  }

  const startingXP = () => {
    return (parseInt(character.Species.StartingAttrs?.Experience) || 0) +
      (parseInt(
        (Array.isArray(character.Species.OptionChoices?.Options) && character.Species.OptionChoices?.Options.length > 0)
          ? character.Species.OptionChoices?.Options?.find((o) => o.Key === character.Species.OptionChoices?.Selected)?.StartingAttributes?.Experience
          : 0
      ) || 0) +
      (character.Obligations[0].XP10 ? 10 : 0) +
      (character.Obligations[0].XP5 ? 5 : 0) +
      (character.Duties[0].XP10 ? 10 : 0) +
      (character.Duties[0].XP5 ? 5 : 0) +
      (character.Morality.XP10 ? 10 : 0) +
      (character.Morality.XPC ? 5 : 0)
  }

  const handleCharacteristicChange = (skillKey, newCharacteristic) => {
    let newSkills = [...character.Skills]
    newSkills.find((s) => s.Key === skillKey).ModChar = newCharacteristic
    handleCharacterChange({ Skills: newSkills })
  };

  const getTotalRanks = (skill) => {
    let totalRanks = skill.value || 0;
    if (freeRanks[skill.Key]) {
      totalRanks += freeRanks[skill.Key];
    }
    return totalRanks;
  };

  const getBaseRanks = (skill) => (skill.value || 0) + (baseFreeRanks[skill.Key] || 0);

  const handleChangeCharacteristics = (characteristic, value, xpChange) => {
    let speciesMin = parseInt(character.Species.StartingChars[characteristic])
    value = Math.min(5, Math.max(speciesMin, value));
    setUsedStartingXP(usedStartingXP - parseInt(xpChange))
    setCharacteristics((prevCharacteristics) => ({
      ...prevCharacteristics,
      [characteristic]: value + (character.Dedications?.[characteristic]?.length || 0),
    }));
    handleCharacterChange({
      Characteristics: { ...characteristics, [characteristic]: value },
      XP: parseInt(character.XP) + parseInt(xpChange),
      UsedStartingXP: usedStartingXP - parseInt(xpChange)
    });
  };

  function isCareerSkill(skillName) {
    if (
      character.CareerSkills.includes(skillName) ||
      character.SpecSkills?.includes(skillName) ||
      character.ExtraCareerSkills.includes(skillName) ||
      character.SelectedCareerSkills[skillName] ||
      character.Specializations.find((s) => s.CareerSkills?.includes(skillName))
    ) {
      return true;
    }
    if (isSpeciesCareerSkill(character.Species, skills.find((s) => s.skill === skillName), skills)) {
      return true;
    }
    return false;
  }

  const handleIncrement = (characteristic) => {
    let dedOffset = hasDedication(characteristic) || 0
    handleChangeCharacteristics(
      characteristic,
      parseInt(characteristics[characteristic]) + (1 - dedOffset),
      -(parseInt(characteristics[characteristic]) + (1 - dedOffset)) * 10,
    );
  };

  const handleDecrement = (characteristic) => {
    let dedOffset = hasDedication(characteristic) || 0
    handleChangeCharacteristics(
      characteristic,
      characteristics[characteristic] - (1 + dedOffset),
      (parseInt(characteristics[characteristic]) - dedOffset) * 10,
    );
  };

  const decreaseSkillValue = (skill) => {
    if (getBaseRanks(skill) > (baseFreeRanks[skill.Key] || 0)) {
      var outOfCareerCost = 0;
      if (!isCareerSkill(skill.skill)) {
        outOfCareerCost = 5;
      }
      skill.value -= 1;
      handleCharacterChange({
        XP: character.XP + (getBaseRanks(skill) + 1) * 5 + outOfCareerCost,
      });
    }
  };

  const increaseSkillValue = (skill) => {
    if (skill.value === undefined) {
      skill.value = 0;
    }
    if (getBaseRanks(skill) < 5) {
      var outOfCareerCost = 0;
      if (!isCareerSkill(skill.skill)) {
        outOfCareerCost = 5;
      }
      skill.value += 1;
      handleCharacterChange({
        XP: character.XP - (getBaseRanks(skill) * 5 + outOfCareerCost),
      });
    }
  };

  useEffect(() => {
    const skillsData = [...character.Skills];
    const groupedSkills = {};
    skillsData.forEach((skill) => {
      if (!groupedSkills[skill.type]) {
        groupedSkills[skill.type] = [];
      }
      groupedSkills[skill.type].push(skill);
    });

    Object.keys(groupedSkills).forEach((type) => {
      groupedSkills[type] = groupedSkills[type].sort((a, b) =>
        a.skill.localeCompare(b.skill),
      );
    });

    const sortedSkills = Object.values(groupedSkills).flat();

    const sortedGeneralSkills = sortedSkills.filter(
      (skill) => skill.type === "General",
    );
    const sortedNonGeneralSkills = sortedSkills.filter(
      (skill) => skill.type !== "General",
    );

    const allSkills = [...sortedGeneralSkills, ...sortedNonGeneralSkills];
    const splitLength = Math.ceil(allSkills.length / 2);
    setLeftSkills(allSkills.slice(0, splitLength))
    setRightSkills(allSkills.slice(splitLength))
  }, [character]);

  function SkillTable() {

    const renderSkillRows = (skillRows, prevType) => {
      const rendered = [];
      let lastType = null;
      skillRows.forEach((skill, index) => {
        if (skill.type !== lastType) {
          lastType = skill.type;
          const carriedOver = index === 0 && skill.type === prevType;
          if (!(carriedOver && isStacked)) {
          rendered.push(
            <TableRow key={`header-${skill.type}`}>
              <TableCell
                colSpan={isSmallScreen ? 5 : 6}
                sx={(theme) => ({
                  fontWeight: "bold",
                  backgroundColor: theme.palette.action.hover,
                  py: 0.95,
                  lineHeight: 1.1,
                })}
              >
                {carriedOver ? " " : skill.type}
              </TableCell>
            </TableRow>
          );
          }
        }
        rendered.push(
          <TableRow key={index}>
            <TableCell>
              <ListItemText primary={skill.skill} />
            </TableCell>
            <TableCell>
              {charMods[skill.Key] ? (
                <Select
                  value={skill.ModChar ? skill.ModChar : skill.characteristic}
                  onChange={(event) => handleCharacteristicChange(skill.Key, event.target.value)}
                  size="small"
                >
                  <MenuItem value={skill.characteristic}>{skill.characteristic}</MenuItem>
                  {!Array.isArray(charMods[skill.Key]) && (<MenuItem value={characteristicsData.find((c) => c.Key === charMods[skill.Key]).Name}>{characteristicsData.find((c) => c.Key === charMods[skill.Key]).Name}</MenuItem>)}
                  {Array.isArray(charMods[skill.Key]) && charMods[skill.Key].map((char) => (
                    <MenuItem value={characteristicsData.find((c) => c.Key === char).Name}>{characteristicsData.find((c) => c.Key === char).Name}</MenuItem>
                  ))}
                </Select>
              ) : (
                <ListItemText secondary={skill.characteristic} />
              )}
            </TableCell>
            {!isSmallScreen && <TableCell>
              <ListItemText secondary={skill.type} />
            </TableCell>}
            <TableCell>
              {isCareerSkill(skill.skill) && (
                <Typography align="center">✔</Typography>
              )}
            </TableCell>
            <TableCell
              style={{
                minWidth: "100px",
              }}
            >
              <div align="center">
                <Button
                  onClick={() => decreaseSkillValue(skill)}
                  style={{
                    fontSize: "0.7rem",
                    minWidth: "30px",
                  }}
                  size="small"
                >
                  -
                </Button>
                {getTotalRanks(skill) || 0}
                <Button
                  onClick={() => increaseSkillValue(skill)}
                  style={{
                    fontSize: "0.7rem",
                    minWidth: "30px",
                  }}
                  size="small"
                >
                  +
                </Button>
              </div>
            </TableCell>
            <TableCell>
              {getDicePool(
                character,
                characteristics[skill.ModChar ? skill.ModChar : skill.characteristic],
                getTotalRanks(skill) || 0,
                skill,
                dieModifiers
              )}
            </TableCell>
          </TableRow>
        );
      });
      return rendered;
    };

    return (
      <Grid container>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={6}
          sx={(theme) => ({
            borderRight: {
              lg: `1px solid ${theme.palette.mode === "light"
                ? lighten(alpha(theme.palette.divider, 1), 0.88)
                : darken(alpha(theme.palette.divider, 1), 0.68)
                }`,
            },
          })}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Skill</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Char</Typography>
                  </TableCell>
                  {!isSmallScreen && <TableCell>
                    <Typography>Type</Typography>
                  </TableCell>}
                  <TableCell>
                    <Typography align="center">Career</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align="center">Adjust Ranks</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Dice Pool</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderSkillRows(leftSkills, null)}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={6}>
          <TableContainer>
            <Table size="small">
              {!isStacked && <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography>Skill</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Char</Typography>
                  </TableCell>
                  {!isSmallScreen && <TableCell>
                    <Typography>Type</Typography>
                  </TableCell>}
                  <TableCell>
                    <Typography align="center">Career</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography align="center">Adjust Ranks</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>Dice Pool</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>}
              <TableBody>
                {renderSkillRows(rightSkills, leftSkills[leftSkills.length - 1]?.type)}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Typography variant="h3" style={{ marginLeft: "30px" }}>
        Characteristics
      </Typography>
      {usedStartingXP > startingXP() && <Typography color="red" sx={{ paddingLeft: '30px' }}>Starting XP limit exceeded by {usedStartingXP - startingXP()}</Typography>}
      {character.Species.StartingChars && (
        <Paper>
          <Grid container spacing={2}>
            {Object.entries(characteristics).map(([key, value]) => (
              <Grid
                item
                xs={6}
                sm={4}
                lg={2}
                key={key}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h5" align="center">
                  {key}
                </Typography>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    variant="contained"
                    onClick={() => handleDecrement(key)}
                    disabled={
                      parseInt(value) ===
                      parseInt(character.Species.StartingChars[key]) + (character.Dedications ? (character.Dedications[key]?.length || 0) : 0)
                    }
                  >
                    -
                  </Button>
                  <span style={{ margin: "0 8px" }}>{value}</span>
                  <Button
                    variant="contained"
                    onClick={() => handleIncrement(key)}
                    disabled={value === 5}
                  >
                    +
                  </Button>
                </div>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
      {!character.Species.StartingChars && (
        <Typography style={{ marginLeft: "30px" }}>
          Select a species to set starting characteristics.
        </Typography>
      )}
      <Typography variant="h3" style={{ marginLeft: "30px" }}>
        Skills
      </Typography>
      <SkillTable />
    </>
  );
}

export default App;
